import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import {
  HiOutlineExclamationTriangle, HiOutlinePlus, HiOutlineTrophy, HiOutlineEllipsisVertical,
} from "react-icons/hi2";
import BotCard from "../components/BotCard";
import BotDetailPage from "../components/BotDetailPage";
import Drawer from "../components/commons/Drawer";
import ActivateBotModal from "../components/ActivateBotModal";
import ActivateLSBotModal from "../components/ActivateLSBotModal";
import CredentialsForm from "../components/CredentialsForm";
import useAuthedApi from "../hooks/useAuthedApi";
import { useCurrentUser } from "../hooks/useCurrentUser";
import Layout from "../components/layout/Layout";
import Badge from "../design-system/badge/Badge.jsx";

// ── Helpers ─────────────────────────────────────────────────────────────────

function fmt(n, dp = 2) {
  const v = parseFloat(n);
  return isNaN(v) ? "0.00" : v.toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

function fmtCompact(n) {
  const v = parseFloat(n);
  if (isNaN(v)) return "0";
  if (Math.abs(v) >= 1000) return (v / 1000).toFixed(1) + "k";
  return v.toFixed(2);
}


function localDateKey(dt) {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function fmtShort(dt) {
  return dt.toLocaleDateString("en", { month: "short", day: "numeric" });
}

function groupChartData(rawData, period) {
  if (!rawData || rawData.length === 0) return [];

  const lastDataDate = rawData.length > 0
    ? new Date(rawData[rawData.length - 1].date + "T00:00:00")
    : new Date();
  lastDataDate.setHours(0, 0, 0, 0);

  if (period === "daily") {
    return rawData.slice(-30).map(d => ({
      label: new Date(d.date + "T00:00:00").toLocaleDateString("en", { month: "short", day: "numeric" }),
      value: d.profit,
    }));
  }

  if (period === "weekly") {
    const weeks = {};
    rawData.slice(-182).forEach(d => {
      const dt = new Date(d.date + "T00:00:00");
      dt.setDate(dt.getDate() - dt.getDay()); // back to Sunday
      const key = localDateKey(dt);
      weeks[key] = (weeks[key] || 0) + d.profit;
    });
    return Object.entries(weeks).slice(-26).map(([date, value]) => {
      const start = new Date(date + "T00:00:00");
      const end = new Date(date + "T00:00:00");
      end.setDate(end.getDate() + 6); // Saturday
      const rangeEnd = end > lastDataDate ? lastDataDate : end;
      const label = start.getMonth() === rangeEnd.getMonth()
        ? `${fmtShort(start)}–${rangeEnd.getDate()}`
        : `${fmtShort(start)}–${fmtShort(rangeEnd)}`;
      return { label, value: Math.round(value * 100) / 100 };
    });
  }

  if (period === "monthly") {
    const months = {};
    rawData.forEach(d => {
      const key = d.date.slice(0, 7);
      months[key] = (months[key] || 0) + d.profit;
    });
    return Object.entries(months).slice(-12).map(([key, value]) => {
      const start = new Date(key + "-01T00:00:00");
      const end = new Date(start.getFullYear(), start.getMonth() + 1, 0); // last day of month
      const rangeEnd = end > lastDataDate ? lastDataDate : end;
      const label = `${fmtShort(start)}–${rangeEnd.getDate()}`;
      return { label, value: Math.round(value * 100) / 100 };
    });
  }

  const years = {};
  rawData.forEach(d => {
    const key = d.date.slice(0, 4);
    years[key] = (years[key] || 0) + d.profit;
  });
  return Object.entries(years).map(([key, value]) => ({
    label: key,
    value: Math.round(value * 100) / 100,
  }));
}


// ── OKX icon ────────────────────────────────────────────────────────────────

function OKXIcon({ size = 28 }) {
  return (
    <div
      className="rounded-sm overflow-hidden shrink-0 grid grid-cols-3 grid-rows-3 bg-black"
      style={{ width: size, height: size, gap: 1.5, padding: 3 }}
    >
      {[0,1,2,3,4,5,6,7,8].map(i => (
        <div key={i} className={i === 4 ? "bg-black" : "bg-white"} style={{ borderRadius: 1 }} />
      ))}
    </div>
  );
}

// ── Section: Borrowing Overview ──────────────────────────────────────────────

function DeleteConfirmModal({ account, onCancel, onConfirm, loading }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.45)" }}
    >
      <div
        className="bg-white rounded-2xl shadow-xl mx-4 w-full max-w-sm p-6 flex flex-col items-center text-center"
        style={{ border: "1px solid #EAEAEA" }}
      >
        <div className="mb-4 flex items-center justify-center rounded-xl" style={{ background: "#FEE2E2", width: 64, height: 64 }}>
          <div className="flex items-center justify-center rounded-full bg-[#DC2626]" style={{ width: 40, height: 40 }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <text x="10" y="15" textAnchor="middle" fill="white" fontSize="16" fontWeight="bold" fontFamily="sans-serif">!</text>
            </svg>
          </div>
        </div>
        <p className="text-sm font-semibold text-[#21272A] mb-6 leading-relaxed">
          Are you sure you want to remove this exchange credentials?
        </p>
        <div className="flex gap-3 w-full">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="flex-1 py-2.5 text-sm font-medium text-[#21272A] bg-white rounded-xl"
            style={{ border: "1px solid #D1D5DB" }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 py-2.5 text-sm font-medium text-white bg-[#DC2626] rounded-xl"
          >
            {loading ? "Removing…" : "Continue to Remove"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BorrowingCard({ account, onEdit, onDelete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="relative bg-[#1C1B1F] rounded-xl p-4 text-white">
      <div className="flex items-center justify-between mb-3">
        <OKXIcon />
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="p-1 rounded-md hover:bg-white/10"
            aria-label="Account options"
          >
            <HiOutlineEllipsisVertical className="w-4 h-4 text-gray-300" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 mt-1 w-28 bg-white rounded-lg shadow-lg border border-gray-200 z-10 py-1">
              <button
                type="button"
                onClick={() => { setMenuOpen(false); onEdit(account); }}
                className="w-full text-left px-3 py-1.5 text-xs text-[#21272A] hover:bg-gray-50"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => { setMenuOpen(false); onDelete(account); }}
                className="w-full text-left px-3 py-1.5 text-xs text-[#DC2626] hover:bg-red-50"
              >
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      <Badge
        color="teal"
        badgeStyle="lighter"
        label={account.is_testnet ? "Testnet" : "Futures"}
        size="small"
      />

      <p className="text-sm font-semibold truncate mt-2">{account.label || "OKX"}</p>
    </div>
  );
}

function BorrowingOverview({ accounts, onEdit, onDelete, onAddAccount }) {
  const tradable = accounts.filter(a => a.is_trading_enabled);

  if (tradable.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-dashed border-gray-300 p-8 text-center mb-6">
        <p className="text-sm text-[#697077] mb-3">No exchange accounts connected yet.</p>
        <button
          onClick={onAddAccount}
          className="text-sm font-medium text-white bg-[#5B3DF6] rounded-xl px-4 py-2"
        >
          + Add OKX Account
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-6">
      <h3 className="text-sm font-semibold text-[#21272A] mb-4">Connect To Your Exchange Securely</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-3">
        {tradable.map(a => (
          <BorrowingCard key={a.id} account={a} onEdit={onEdit} onDelete={onDelete} />
        ))}
        {/* Non-trading accounts */}
        {accounts.filter(a => !a.is_trading_enabled).map(a => (
          <BorrowingCard key={a.id} account={a} onEdit={onEdit} onDelete={onDelete} />
        ))}
      </div>
      <button
        type="button"
        onClick={onAddAccount}
        className="w-full flex items-center justify-center gap-2 border border-gray-200 rounded-xl py-2.5 text-sm font-medium text-[#21272A]"
      >
        Add Exchange <HiOutlinePlus className="w-4 h-4" />
      </button>
    </div>
  );
}

// ── Section: Your WINS Today ─────────────────────────────────────────────────

const PIE_COLORS = ["#5B3DF6","#E91E63","#00BCD4","#FF9800","#FFC107"];

function MedalIcon({ rank }) {
  if (rank === 1) return <span className="text-lg">🥇</span>;
  if (rank === 2) return <span className="text-lg">🥈</span>;
  if (rank === 3) return <span className="text-lg">🥉</span>;
  return (
    <span className="w-6 h-6 rounded-full bg-gray-100 text-xs font-bold text-[#697077] flex items-center justify-center shrink-0">
      {rank}
    </span>
  );
}


function BotRankRow({ bot, rank }) {
  const pair = (bot.symbol || "").replace(/USDT$/, "/USDT");
  const initials = (bot.symbol || "?").replace("USDT", "").slice(0, 3).toUpperCase();
  const pnl = parseFloat(bot.realized_pnl || 0);
  const d = (bot.direction || "").toUpperCase();
  const isDualSide = d === "LS2" || d === "LS" || d === "LS6";
  // initial_capital is stored AFTER leverage for LONG and LS/LS2/LS6 (same
  // convention as BotDetailPage.jsx/BotCard.jsx) -- normalize here too so
  // this PnL% matches what the detail page shows for the same bot.
  const rawCapital = parseFloat(bot.initial_capital || 0);
  const capital = ((d === "LONG" || isDualSide) && bot.leverage) ? rawCapital / bot.leverage : rawCapital;
  const pnlPct = capital > 0 ? (pnl / capital) * 100 : 0;
  const dirColor = d === "SHORT" ? "pink" : isDualSide ? "purple" : "teal";
  const dirLabel = d === "SHORT" ? "SHORT" : isDualSide ? "L&S" : "LONG";
  const isLive = bot.status === "running";

  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-gray-100 last:border-0">
      <MedalIcon rank={rank} />

      {/* Coin icon */}
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-[9px] font-bold text-white shrink-0">
        {initials}
      </div>

      {/* Pair + status */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-sm font-semibold text-[#21272A]">{pair}</span>
          <Badge color={dirColor} badgeStyle="filled" label={dirLabel} size="small" />
          {isLive
            ? <Badge color="green" type="dot" label="LIVE" size="small" />
            : <Badge color="gray" badgeStyle="lighter" label={bot.status.toUpperCase()} size="small" />
          }
        </div>
        <p className="text-[10px] text-[#697077] truncate">{bot.credential_label || "Xenoarc"}</p>
      </div>

      {/* Stats */}
      <div className="text-right shrink-0">
        <p className="text-xs font-semibold text-[#21272A]">{fmt(pnl)} USDT</p>
        <p className={`text-[10px] font-medium ${pnlPct >= 0 ? "text-[#0F6D32]" : "text-red-500"}`}>
          {pnlPct >= 0 ? "+" : ""}{pnlPct.toFixed(2)}%
        </p>
      </div>
    </div>
  );
}

function WinsToday({ topBots, loading, onAddBot }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");

  const filtered = topBots.filter(b => filter === "all" || b.direction === filter);
  const pieData = filtered
    .filter(b => parseFloat(b.realized_pnl) > 0)
    .map((b, i) => ({
      name: b.symbol.replace("USDT", "/USDT"),
      value: parseFloat(b.realized_pnl),
      color: PIE_COLORS[i % PIE_COLORS.length],
    }));

  const totalPnl = filtered.reduce((s, b) => s + parseFloat(b.realized_pnl || 0), 0);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-base font-semibold text-[#21272A]">Your WINS Today!</h3>
        <button
          onClick={() => navigate("/bot")}
          className="flex items-center gap-1 text-xs font-medium text-white bg-[#5B3DF6] rounded-lg px-3 py-1.5"
        >
          <HiOutlinePlus className="w-3.5 h-3.5" />
          Add Bot
        </button>
      </div>
      <p className="text-[11px] text-[#697077] mb-4">
        This Bot performing based on Avg. Monthly Profit
      </p>

      {/* Filter */}
      <div className="flex items-center justify-end mb-3">
        <span className="text-[11px] text-[#697077] mr-2">Filter By</span>
        <select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="text-[11px] border border-gray-200 rounded-lg px-2 py-1 text-[#21272A] bg-white"
        >
          <option value="all">All Bot</option>
          <option value="LONG">Long</option>
          <option value="SHORT">Short</option>
          <option value="LS6">L&S</option>
        </select>
      </div>

      {loading ? (
        <div className="h-40 flex items-center justify-center text-sm text-[#697077]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="h-40 flex flex-col items-center justify-center gap-3 text-center">
          <HiOutlineTrophy className="w-8 h-8 text-gray-300" />
          <p className="text-sm text-[#697077]">
            No bots active yet — activate your first BOT to see performance.
          </p>
          <button
            onClick={onAddBot}
            className="text-sm font-medium text-[#5B3DF6] hover:underline"
          >
            + Activate BOT
          </button>
        </div>
      ) : (
        <div className="flex flex-col-reverse desktop:flex-row gap-6">
          {/* Leaderboard */}
          <div className="flex-1 min-w-0">
            {filtered.map((bot, i) => (
              <BotRankRow key={bot.instance_id} bot={bot} rank={i + 1} />
            ))}
          </div>

          {/* Donut chart */}
          <div className="flex flex-col items-center shrink-0 desktop:shrink-0">
            {pieData.length > 0 ? (
              <>
                <PieChart width={160} height={160}>
                  <Pie
                    data={pieData}
                    cx={75} cy={75}
                    innerRadius={45} outerRadius={72}
                    dataKey="value"
                    strokeWidth={2}
                    stroke="#fff"
                  >
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
                <p className="text-[10px] text-[#697077] text-center mt-1">
                  Trending {totalPnl >= 0 ? "up" : "down"} this month
                </p>
                <p className="text-[9px] text-[#9CA3AF] text-center">
                  Showing total all BOTs for the last 6 months
                </p>
              </>
            ) : (
              <div className="w-40 h-40 rounded-full border-[12px] border-gray-100 flex items-center justify-center">
                <span className="text-[10px] text-[#9CA3AF]">No data</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Section: Profit Trends ───────────────────────────────────────────────────

const CHART_TABS = [
  { key: "daily",   label: "Daily"   },
  { key: "weekly",  label: "Weekly"  },
  { key: "monthly", label: "Monthly" },
  { key: "yearly",  label: "Yearly"  },
];

function ProfitTrends({ summary, chartRaw, loading }) {
  const [view, setView] = useState("profits");
  const [period, setPeriod] = useState("daily");

  const chartData = useMemo(() => {
    const mapped = groupChartData(chartRaw, period).slice(-31);
    let cum = 0;
    return mapped.map(d => ({ ...d, value: Math.round((cum += d.value) * 100) / 100 }));
  }, [chartRaw, period]);

  const dailyChartData = useMemo(() => {
    return chartRaw.slice(-30).map(d => ({
      label: new Date(d.date + "T00:00:00").toLocaleDateString("en", { month: "short", day: "numeric" }),
      value: d.profit,
    }));
  }, [chartRaw]);

  const total = summary?.total_profit ?? 0;
  const monthly = summary?.monthly_profit ?? 0;
  const yearly = useMemo(() => chartRaw.reduce((sum, d) => sum + (d.profit || 0), 0), [chartRaw]);
  const daily = chartRaw.length > 0 ? (chartRaw[chartRaw.length - 1].profit || 0) : 0;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm text-xs">
        <p className="font-semibold text-[#21272A]">{label}</p>
        <p className="text-[#5B3DF6]">Realized Profit: {fmt(payload[0].value)} USDT</p>
      </div>
    );
  };

  const emptyChart = (
    <div className="h-48 flex flex-col items-center justify-center gap-2">
      <div className="w-full h-1 bg-gray-100 rounded-full" />
      <p className="text-xs text-[#9CA3AF]">No trade history yet — stats will appear after your first completed trade.</p>
    </div>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
      {/* Tab: Profit Trends / Daily Profit */}
      <div className="flex items-center gap-1 mb-4 border-b border-gray-100 pb-3">
        {[{ key: "profits", label: "Profit Trends" }, { key: "daily", label: "Daily Profit" }].map(t => (
          <button
            key={t.key}
            onClick={() => setView(t.key)}
            className={`text-sm font-medium px-3 py-1.5 rounded-lg transition-colors ${
              view === t.key
                ? "bg-gray-100 text-[#21272A]"
                : "text-[#697077] hover:text-[#21272A]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="h-48 flex items-center justify-center text-sm text-[#697077]">Loading…</div>
      ) : view === "profits" ? (
        <>
          {/* Summary */}
          <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
            <div>
              <p className="text-[9px] font-semibold text-[#697077] uppercase tracking-wide mb-0.5">Total Profit</p>
              <p className="text-2xl font-bold text-[#21272A]">{fmt(total)} USDT</p>
            </div>
            {/* Period tabs */}
            <div className="flex items-center gap-1">
              {CHART_TABS.map(t => (
                <button
                  key={t.key}
                  onClick={() => setPeriod(t.key)}
                  className={`text-xs px-2.5 py-1 rounded-lg transition-colors ${
                    period === t.key
                      ? "bg-[#5B3DF6] text-white font-semibold"
                      : "text-[#697077] hover:text-[#21272A]"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Area chart — realized profit per period, max 31 points */}
          {chartData.length === 0 ? emptyChart : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#5B3DF6" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#5B3DF6" stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#9CA3AF" }} axisLine={false} tickLine={false} tickFormatter={v => fmtCompact(v)} width={40} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="value" stroke="#5B3DF6" strokeWidth={2} fill="url(#profitGrad)" dot={false} activeDot={{ r: 4, fill: "#5B3DF6" }} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </>
      ) : (
        /* Daily Profit — bar chart, last 30 days, no summary/stats/period tabs */
        <>
          {dailyChartData.length === 0 ? emptyChart : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={dailyChartData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#9CA3AF" }} axisLine={false} tickLine={false} tickFormatter={v => fmtCompact(v)} width={40} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" radius={[3, 3, 0, 0]} maxBarSize={24}>
                  {dailyChartData.map((entry, i) => (
                    <Cell key={i} fill={entry.value < 0 ? "#E91E63" : "#5B3DF6"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </>
      )}
    </div>
  );
}

// ── Section: Active BOT ──────────────────────────────────────────────────────

function ActiveBotSection({ bots, onView, loading }) {
  const running = bots.filter(b => b.status === "running" && !b.config_snapshot?.dry_run);
  if (!loading && running.length === 0) return null;

  return (
    <div className="mb-6">
      <h3 className="text-xs font-semibold text-[#697077] uppercase tracking-widest mb-3">
        Active BOT
      </h3>
      {loading ? (
        <div className="text-sm text-[#697077]">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 desktop:grid-cols-3 gap-4">
          {running.map(bot => (
            <BotCard key={bot.instance_id} bot={bot} onView={onView} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Dashboard (main) ─────────────────────────────────────────────────────────

export default function Dashboard() {
  const { user, status: authStatus } = useCurrentUser();
  const { authedGet, authedDelete } = useAuthedApi();
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState([]);
  const [bots, setBots] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [botsLoading, setBotsLoading] = useState(true);

  const [editingAccount, setEditingAccount] = useState(null);
  const [deletingAccount, setDeletingAccount] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [viewingBot, setViewingBot] = useState(null);
  const [lsModalOpen, setLsModalOpen] = useState(false);
  const [activatingDirection, setActivatingDirection] = useState(null);

  const refresh = useCallback(() => {
    setBotsLoading(true);
    setAnalyticsLoading(true);

    authedGet("/api/borrow/credentials/list/")
      .then(r => setAccounts(r.data))
      .catch(() => {})
      .finally(() => setDataLoaded(true));

    authedGet("/api/borrow/list/")
      .then(r => setBots(r.data))
      .catch(() => {})
      .finally(() => setBotsLoading(false));

    authedGet("/api/borrow/analytics/")
      .then(r => setAnalytics(r.data))
      .catch(() => setAnalytics(null))
      .finally(() => setAnalyticsLoading(false));
  }, [authedGet]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingAccount) return;
    setDeleteLoading(true);
    try {
      await authedDelete(`/api/borrow/credentials/${deletingAccount.id}/delete/`);
      setDeletingAccount(null);
      refresh();
    } catch {
      // leave modal open so user can retry
    } finally {
      setDeleteLoading(false);
    }
  }, [deletingAccount, authedDelete, refresh]);

  useEffect(() => {
    if (authStatus === "authed") {
      refresh();
    }
  }, [authStatus, refresh]);

  if (authStatus === "loading" || (authStatus === "authed" && !dataLoaded)) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-[#697077]">Loading…</div>;
  }
  if (authStatus === "anonymous") return <Navigate to="/login" replace />;

  if (dataLoaded && accounts.length === 0) {
    return (
      <Layout user={user}>
        <div className="flex items-center gap-2 px-4 py-2.5 mb-6 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm">
          <HiOutlineExclamationTriangle className="w-4 h-4 shrink-0" />
          <span>You are in Leverage Market — higher risk. Manage positions carefully.</span>
          <a href="#" className="ml-auto text-sm font-medium text-amber-800 underline underline-offset-2 shrink-0">
            Learn More
          </a>
        </div>
        <div className="flex items-start justify-center">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 max-w-lg mx-auto">
            <h2 className="text-sm font-semibold text-[#21272A] mb-4">Connect To Your Exchange Securely</h2>
            <CredentialsForm suggestedLabel="OKX" onSaved={refresh} />
          </div>
        </div>
      </Layout>
    );
  }

  if (viewingBot) {
    return (
      <Layout user={user}>
        <BotDetailPage
          bot={viewingBot}
          onBack={() => setViewingBot(null)}
          onStop={() => { refresh(); setViewingBot(null); }}
        />
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <BorrowingOverview
        accounts={accounts}
        onEdit={a => setEditingAccount(a)}
        onDelete={a => setDeletingAccount(a)}
        onAddAccount={() => navigate("/connect-exchange")}
      />
      {deletingAccount && (
        <DeleteConfirmModal
          account={deletingAccount}
          onCancel={() => setDeletingAccount(null)}
          onConfirm={handleDeleteConfirm}
          loading={deleteLoading}
        />
      )}

      <WinsToday
        topBots={analytics?.top_bots ?? []}
        loading={analyticsLoading}
        onAddBot={() => navigate("/bot")}
      />

      <ProfitTrends
        summary={analytics?.summary}
        chartRaw={analytics?.chart_data ?? []}
        loading={analyticsLoading}
      />

      <ActiveBotSection
        bots={bots}
        onView={setViewingBot}
        loading={botsLoading}
      />

      {/* Modals */}
      <ActivateBotModal
        open={!!activatingDirection}
        direction={activatingDirection || "Long"}
        onClose={() => setActivatingDirection(null)}
        accounts={accounts}
        onStarted={() => { setActivatingDirection(null); refresh(); }}
      />
      <ActivateLSBotModal
        open={lsModalOpen}
        onClose={() => setLsModalOpen(false)}
        accounts={accounts}
        onStarted={() => { setLsModalOpen(false); refresh(); }}
      />

      {/* Edit account drawer */}
      <Drawer
        open={!!editingAccount}
        onClose={() => setEditingAccount(null)}
        title="Edit OKX Credentials"
      >
        <CredentialsForm
          existing={editingAccount}
          onSaved={() => { setEditingAccount(null); refresh(); }}
        />
      </Drawer>
    </Layout>
  );
}
